// #region Imports
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import LoadingButton from "_shared/_views/loading/loading-button";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import YearInput from "_shared/dates/years/_views/year-input";
import Year from "_shared/dates/years/year";
import Guid from "_shared/guids/guid";
import normalizeURLInput from "_shared/urls/normalize-url-input";
import URLInput from "_shared/urls/url-input";
import { CompanyProfileTypes, defaultProfileName } from "config";
import AvatarUpload, { AvatarUploadValue } from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import ProfileNameInput from "marketplace/_views/profiles/profile-name-input";
import VisibilityToggleSwitch, {
  buildVisibilityConfirmMessage,
  buildVisibilityConfirmTitle,
  buildVisibilitySuccessMessage,
} from "marketplace/_views/profiles/visibility-toggle-switch";
import {
  getCompanyAvatar,
  getCompanyById,
} from "marketplace/companies/_store/companies-redux-slice";
import { companiesApi } from "marketplace/companies/_api/company-api";
import Company from "marketplace/companies/company";
import CompanyProfileContactPersonInput from "marketplace/companies/profiles/_views/inputs/company-profile-contact-person-input";
import CompanyProfileNumEmployeesInput from "marketplace/companies/profiles/_views/inputs/company-profile-num-employees-input";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import Individual from "marketplace/individuals/individual";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { HttpStatusCode } from "axios";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  // The dialog hands the form a bounded height, so the fields scroll inside it and the save button
  // keeps a row of its own at the bottom instead of floating over whatever is under it.
  maxHeight: "100%",
  minHeight: 0,
  width: "100%",
}));
const FormScrollArea = styled("div")(({ theme }) => ({
  display: "grid",
  flex: "1 1 auto",
  gridTemplateRows: "repeat(4, auto)",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  // MUI hangs a switch's control 11px outside its own row, so that the track lines up with the body
  // text beside it. This area hides its overflow, which cut the hover halo off at the edge — so the
  // room the control is designed to hang into is given here, where every row shares it and the
  // alignment the negative margin buys is kept.
  paddingLeft: theme.spacing(1.5),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(1, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const AboutYou = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "3",
  marginTop: theme.spacing(1),
}));
const AboutYouTextField = styled(TextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));
const YearEstablishedInput = styled(YearInput)(({ theme }) => ({
  minWidth: theme.spacing(20),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const NumEmployeesInput = styled(CompanyProfileNumEmployeesInput)(({ theme }) => ({
  minWidth: theme.spacing(20),
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactPersonInput = styled(CompanyProfileContactPersonInput)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const CompanyWebsite = styled(URLInput)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const CompanyLocation = styled(PlaceAutocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const CompanyDetails = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "4",
  // Clear of the button row's divider, so the switch is not pressed against it.
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flex: "0 0 auto",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  padding: theme.spacing(2, 0),
  width: "100%",
}));
const SaveButtonWrapper = styled("span")(() => ({
  alignSelf: "flex-start",
  display: "inline-flex",
}));
const SaveValidationMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
const CategoriesSelector = styled(ProfileCategoriesSelect)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
  },
}));
// #endregion

// #region Component Props
export type EditCompanyProfileProps = {
  isVisible?: boolean;
  companyId?: Guid;
  profile?: CompanyProfile;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (company: Company) => void;
};
// #endregion

function createNewProfile(): CompanyProfile {
  return CompanyProfile.create({
    name: "New Profile",
    categories: [],
  });
}

function isProfileValid(profile: CompanyProfile): boolean {
  const hasCategories = profile.categories.length > 0;
  const hasName = Boolean(profile.name);
  const hasContactPerson = Boolean(profile.contactUserId);
  return hasCategories && hasName && hasContactPerson;
}

function withContactUserIdFallback(profile: CompanyProfile, contactUserId?: Guid): CompanyProfile {
  if (profile.contactUserId || !contactUserId) return profile;
  return new CompanyProfile({
    ...profile,
    contactUserId,
  });
}

export default function EditCompanyProfile(props: Readonly<EditCompanyProfileProps>) {
  const { profile, onFormDirty, onSave } = props;
  const initialUpdatedProfile = props.companyId ? profile : createNewProfile();

  // #region State
  const [saving, setSaving] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);

  const [companyId, setCompanyId] = useState<Guid | undefined>(props.companyId);
  const [originalCompanyProfile, setOriginalCompanyProfile] = useState<CompanyProfile | undefined>(profile);
  const [updatedCompanyProfile, setUpdatedCompanyProfile] = useState<CompanyProfile | undefined>(initialUpdatedProfile);
  const [updatedAvatar, setUpdatedAvatar] = useState<AvatarUploadValue | undefined>();

  const [formCompanyName, setFormCompanyName] = useState<string>(profile?.name ?? "");
  const [formContactPerson, setFormContactPerson] = useState<Individual | IndividualSummary | null>(null);
  const [formYearEstablished, setFormYearEstablished] = useState<string>(profile?.yearEstablished?.toString() ?? "");
  const [formNumberOfEmployees, setFormNumberOfEmployees] = useState<string>(
    profile?.numberOfEmployees?.toString() ?? "",
  );
  const [formWebsiteUrl, setFormWebsiteUrl] = useState<string>(profile?.website?.toString() ?? "");
  const [formLocation, setFormLocation] = useState<Address | null>(profile?.location ?? null);
  const [formCategories, setFormCategories] = useState<string[]>(profile?.categories ?? []);
  const [formDescription, setFormDescription] = useState<string>(profile?.description ?? "");
  const [formValidated, setFormValidated] = useState(() =>
    initialUpdatedProfile ? isProfileValid(initialUpdatedProfile) : false,
  );
  const [profileHash, setProfileHash] = useState<string>(() =>
    initialUpdatedProfile ? JSON.stringify(initialUpdatedProfile.toJSON()) : "",
  );
  const [locationServicesUnavailable, setLocationServicesUnavailable] = useState<boolean>(false);

  // #region Hooks
  const session = useSession();
  const confirm = useConfirmDialog();

  // #region Selectors
  const company = useSelector(getCompanyById(companyId));
  const { isLoading, error: errorLoading } = companiesApi.useGetCompanyByIdQuery(
    { companyId: companyId?.value ?? "" },
    { skip: !companyId || !!company }
  );
  const existingAvatar = useSelector(getCompanyAvatar(companyId ?? null, "small"));
  companiesApi.useGetCompanyAvatarQuery(
    { companyId: companyId?.value ?? "", size: "small" },
    { skip: !companyId || !company?.profile?.avatarId || !!existingAvatar }
  );

  // Mutations
  const [createCompanyMutation] = companiesApi.useCreateCompanyMutation();
  const [updateCompanyMutation] = companiesApi.useUpdateCompanyMutation();
  const [createCompanyAvatarMutation] = companiesApi.useCreateCompanyAvatarMutation();
  const [refreshCompany] = companiesApi.useLazyGetCompanyByIdQuery();

  const locationServicesRef = useRef<boolean>(false);
  const hydratedProfileRef = useRef<CompanyProfile | undefined>(initialUpdatedProfile);
  const preserveEditsDuringConflictRefreshRef = useRef(false);

  // #region Validation
  function validateProfile(profile: CompanyProfile) {
    if (!profile) return;
    setFormValidated(isProfileValid(profile));
  }

  const isFormDirty = useMemo(() => {
    if (!updatedCompanyProfile) return false;

    const hash = JSON.stringify(updatedCompanyProfile.toJSON());
    const hasProfileChanges = hash !== profileHash;
    const hasAvatarChanges = Boolean(updatedAvatar);
    return hasProfileChanges || hasAvatarChanges;
  }, [profileHash, updatedAvatar, updatedCompanyProfile]);

  /**
   * Visibility is a field of this form now rather than a switch that writes on its own, so it has to
   * count towards whether there is anything to save. Beside the memo above rather than inside it, so
   * that memo keeps the manual memoization it still has.
   */
  const isVisibilityDirty = Boolean(companyId) && isVisible !== (company?.isVisible ?? false);
  const hasUnsavedChanges = isFormDirty || isVisibilityDirty;

  const canSave = (): boolean => {
    const locationIsBlocking = locationServicesUnavailable && !formLocation;
    return !isLoading && !saving && hasUnsavedChanges && formValidated && !locationIsBlocking;
  };

  const hasProfileName = Boolean(updatedCompanyProfile?.name);
  const isProfileTypeSelected = (updatedCompanyProfile?.categories.length ?? 0) > 0;
  const isContactPersonSelected = Boolean(updatedCompanyProfile?.contactUserId);
  const locationIsBlockingSave = locationServicesUnavailable && !formLocation;
  const shouldShowSaveValidationMessage = Boolean(
    updatedCompanyProfile
      && !isLoading
      && !saving
      && hasUnsavedChanges
      && !locationIsBlockingSave
      && hasProfileName,
  );
  const saveValidationMessages = [
    shouldShowSaveValidationMessage && !isProfileTypeSelected
      ? "Select at least one profile type to enable save."
      : "",
    shouldShowSaveValidationMessage && !isContactPersonSelected
      ? "Select a contact person to enable save."
      : "",
  ].filter(Boolean);
  const saveValidationMessage = saveValidationMessages.join(" ");
  // #endregion

  // #region Lifecycle
  useEffect(() => {
    onFormDirty?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onFormDirty]);

  useEffect(() => {
    if (!companyId) return;

    // No company/profile
    if (!company?.profile) return;
    const companyProfile = withContactUserIdFallback(company.profile, company.contactId);

    if (preserveEditsDuringConflictRefreshRef.current) {
      preserveEditsDuringConflictRefreshRef.current = false;
      hydratedProfileRef.current = companyProfile;
      setOriginalCompanyProfile(companyProfile);
      setProfileHash(JSON.stringify(companyProfile.toJSON()));
      return;
    }

    // Nothing changed
    if (hydratedProfileRef.current === companyProfile) return;

    // Populate form with company profile data
    const hash = JSON.stringify(companyProfile.toJSON());
    let active = true;
    if (hash === profileHash) {
      hydratedProfileRef.current = companyProfile;
      queueMicrotask(() => {
        if (active) setIsVisible(company.isVisible);
      });
      return () => {
        active = false;
      };
    }

    queueMicrotask(() => {
      if (!active) return;

      hydratedProfileRef.current = companyProfile;
      setOriginalCompanyProfile(companyProfile);
      setUpdatedCompanyProfile(companyProfile);
      setIsVisible(company.isVisible);
      setFormCompanyName(companyProfile.name ?? "Unknown Company");
      setFormYearEstablished(companyProfile.yearEstablished?.toString() ?? "");
      setFormNumberOfEmployees(companyProfile.numberOfEmployees?.toString() ?? "");
      setFormWebsiteUrl(companyProfile.website?.toString() ?? "");
      setFormLocation(companyProfile.location ?? null);
      setFormCategories(companyProfile.categories ?? []);
      setFormDescription(companyProfile.description ?? "");
      validateProfile(companyProfile);
      setProfileHash(hash);
    });

    return () => {
      active = false;
    };
  }, [company, companyId, profileHash]);

  useEffect(() => {
    if (errorLoading) {
      enqueueSnackbar("Profile currently unavailable", { variant: "warning" });
    }
  }, [errorLoading]);
  // #endregion

  // #region Functions
  function constructCompany(): Company {
    if (!session.company?.legalEntityId) throw new Error("Company EntityId not found");
    if (!updatedCompanyProfile) throw new Error("Company profile not found");

    return new Company({
      id: companyId ?? Guid.generate(),
      legalEntityId: session.company?.legalEntityId,
      profile: updatedCompanyProfile,
      contactId: formContactPerson?.id,
      isVisible,
    });
  }

  async function handleSaveButtonClick() {
    // Before either save: publishing to the marketplace is worth asking about, and the switch no
    // longer writes anything on its own, so this is the only point at which it takes effect.
    const maySave = await confirmVisibilityChange();
    if (!maySave) return;

    if (companyId) await updateProfile();
    else await createProfile();
  }

  /** Asks before a save that would change who can see the profile. */
  async function confirmVisibilityChange(): Promise<boolean> {
    if (!isVisibilityDirty) return true;

    const profileName = company?.profile?.name ?? "this";
    const response = await confirm({
      title: buildVisibilityConfirmTitle(isVisible),
      message: buildVisibilityConfirmMessage(profileName, isVisible),
    });

    return response !== ConfirmResponse.Cancel;
  }

  async function updateProfile() {
    if (!companyId || !originalCompanyProfile || !updatedCompanyProfile) return;

    const wasVisibilityChanged = isVisibilityDirty;
    const savedProfileName = updatedCompanyProfile.name ?? "this";

    try {
      setSaving(true);
      if (updatedAvatar) {
        await createCompanyAvatarMutation({
          companyId: companyId.value,
          file: updatedAvatar.file,
          crop: updatedAvatar.crop,
        }).unwrap();
        // The upload is its own committed mutation. Do not replay it if the profile write below
        // conflicts or fails and the user chooses to try the profile save again.
        setUpdatedAvatar(undefined);
      }
      const contactPersonChanged = !originalCompanyProfile.contactUserId?.isEqualTo(updatedCompanyProfile.contactUserId);
      const returnedCompanyJson = await updateCompanyMutation({
        companyId: companyId.value,
        isVisible,
        contactId: contactPersonChanged ? formContactPerson?.id : undefined,
        originalProfile: originalCompanyProfile,
        updatedProfile: updatedCompanyProfile,
      }).unwrap();
      const returnedCompany = Company.fromJSON(returnedCompanyJson);

      setOriginalCompanyProfile(returnedCompany.profile);
      setUpdatedCompanyProfile(returnedCompany.profile);
      setUpdatedAvatar(undefined);

      enqueueSnackbar("Profile changes saved", { variant: "success" });
      if (wasVisibilityChanged) {
        enqueueSnackbar(buildVisibilitySuccessMessage(savedProfileName, returnedCompany.isVisible === true), {
          variant: "info",
        });
      }
      onSave?.(returnedCompany);
    } catch (error) {
      if (getApiErrorStatus(error) === HttpStatusCode.Conflict) {
        try {
          preserveEditsDuringConflictRefreshRef.current = true;
          const refreshedJson = await refreshCompany({ companyId: companyId.value }, false).unwrap();
          const refreshedCompany = Company.fromJSON(refreshedJson);
          if (!refreshedCompany.profile) {
            preserveEditsDuringConflictRefreshRef.current = false;
            enqueueSnackbar("The company contact changed, but the refreshed profile is unavailable.", {
              variant: "error",
            });
            return;
          }
          const refreshedProfile = withContactUserIdFallback(
            refreshedCompany.profile,
            refreshedCompany.contactId,
          );
          // Rebase unrelated local edits onto the server's current contact. The conflicting
          // selection itself must not remain queued: the next write requires the user to choose
          // a contact again, or to save only their other edits against this refreshed baseline.
          const rebasedProfile = new CompanyProfile({
            ...updatedCompanyProfile,
            contactUserId: refreshedProfile.contactUserId,
          });
          hydratedProfileRef.current = refreshedProfile;
          setOriginalCompanyProfile(refreshedProfile);
          setUpdatedCompanyProfile(rebasedProfile);
          setFormContactPerson(null);
          validateProfile(rebasedProfile);
          setProfileHash(JSON.stringify(refreshedProfile.toJSON()));
          enqueueSnackbar("The company contact changed. Review the refreshed state, then save again.", {
            variant: "warning",
          });
          return;
        } catch {
          preserveEditsDuringConflictRefreshRef.current = false;
          enqueueSnackbar("The company contact changed, but the refreshed profile is unavailable.", {
            variant: "error",
          });
          return;
        }
      }
      enqueueSnackbar("Unable to update profile", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function createProfile() {
    try {
      setSaving(true);
      const companyToCreate: Company = constructCompany();

      const returnedCompanyJson = await createCompanyMutation({
        company: companyToCreate,
      }).unwrap();

      const returnedCompany = Company.fromJSON(returnedCompanyJson);
      enqueueSnackbar("Company profile created", { variant: "success" });
      onSave?.(returnedCompany);

      setCompanyId(returnedCompany.id);
      setOriginalCompanyProfile(returnedCompany.profile);
      setUpdatedCompanyProfile(returnedCompany.profile);

      if (updatedAvatar) {
        await createAvatar(returnedCompany.id, updatedAvatar);
        setUpdatedAvatar(undefined);
      }

      const hash = JSON.stringify(returnedCompanyJson);
      setProfileHash(hash);
    } catch {
      enqueueSnackbar("Unable to create profile", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function createAvatar(companyId: Guid, avatar: AvatarUploadValue) {
    if (!companyId || !avatar) return;

    try {
      await createCompanyAvatarMutation({
        companyId: companyId.value,
        file: avatar.file,
        crop: avatar.crop,
      }).unwrap();
    } catch {
      throw new Error("Unable to create avatar");
    }
  }

  // #endregion

  // #region Render
  return (
    <MainContainer>
      <FormScrollArea data-testid="profile-form-fields">
        <HeaderContainer>
          <AvatarNameContainer>
            <AvatarUpload
              accept="image/bmp, image/gif, image/heic, image/heif, image/jpeg, image/png, image/webp"
              avatar={existingAvatar ?? updatedAvatar}
              onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
            />
            <ProfileNameInput
              name={formCompanyName}
              isLoading={isLoading}
              error={formCompanyName === defaultProfileName && companyId && !isLoading}
              onNameChange={(updatedName: string) => {
                setFormCompanyName(updatedName);
                if (!updatedCompanyProfile) return;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  name: updatedName,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
          </AvatarNameContainer>
          <CategoriesSelector
            options={CompanyProfileTypes}
            categories={formCategories}
            onChange={async (updatedCategories: string[]) => {
              setFormCategories(updatedCategories);
              if (!updatedCompanyProfile) return;
              if (updatedCategories === updatedCompanyProfile.categories) return;
              const updatedProfile = new CompanyProfile({
                ...updatedCompanyProfile,
                categories: updatedCategories,
              });
              setUpdatedCompanyProfile(updatedProfile);
              validateProfile(updatedProfile);
            }}
          />
        </HeaderContainer>
        {!isLoading && (
          <ContentContainer>
            <ContactPersonInput
              required
              contactUserId={updatedCompanyProfile?.contactUserId}
              onChange={(value: Individual | IndividualSummary | null) => {
                setFormContactPerson(value);
                if (!updatedCompanyProfile) return;
                if (!value) {
                  if (!updatedCompanyProfile.contactUserId) return;
                  const updatedProfile = new CompanyProfile({
                    ...updatedCompanyProfile,
                    contactUserId: undefined,
                  });
                  setUpdatedCompanyProfile(updatedProfile);
                  validateProfile(updatedProfile);
                  return;
                }
                if (updatedCompanyProfile.contactUserId?.isEqualTo(value.userId)) return;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  contactUserId: value.userId,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
            <YearEstablishedInput
              label="Year Established"
              variant="outlined"
              year={Year.fromInput(formYearEstablished)}
              cannotBeInFuture
              onChange={(newYear?: Year) => {
                if (!newYear) return;
                setFormYearEstablished(newYear.string);
                if (!updatedCompanyProfile) return;
                if (newYear.value === updatedCompanyProfile.yearEstablished) return;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  yearEstablished: newYear.value,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
            <NumEmployeesInput
              numEmployees={formNumberOfEmployees}
              onChange={(employeeNumberValue: string) => {
                const trimmedValue = employeeNumberValue.trim();
                setFormNumberOfEmployees(trimmedValue);
                if (!updatedCompanyProfile) return;
                if (employeeNumberValue === updatedCompanyProfile.numberOfEmployees?.toString()) return;
                const numberOfEmployees = trimmedValue.length ? Number.parseInt(trimmedValue) : 0;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  numberOfEmployees: Math.max(numberOfEmployees, 0),
                });

                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
            <CompanyWebsite
              variant="outlined"
              url={formWebsiteUrl?.toString()}
              onChange={(url: string, error?: string) => {
                setFormWebsiteUrl(url);
                if (error) setFormValidated(false);
                if (!updatedCompanyProfile || !url || error) return;
                const normalizedUrl = normalizeURLInput(url) ?? "";
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  website: normalizedUrl,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
            <CompanyLocation
              label="Location"
              variant="outlined"
              value={formLocation}
              locationServicesDisabledRef={locationServicesRef}
              disableManualEntryWhenServicesUnavailable
              onLocationServicesDisabledChange={(newValue) => {
                locationServicesRef.current = newValue;
                setLocationServicesUnavailable(newValue);
              }}
              onChange={(value: Address | null) => {
                setFormLocation(value);
                if (!updatedCompanyProfile || value?.isEqualTo(updatedCompanyProfile.location)) return;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  location: value ?? undefined,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
            />
          </ContentContainer>
        )}
        {!isLoading && (
          <AboutYou>
            <Typography variant="h6">About Your Company</Typography>
            <AboutYouTextField
              id="about-you-textfield"
              value={formDescription}
              onChange={(event) => {
                setFormDescription(event.target.value);
                if (!updatedCompanyProfile) return;
                if (event.target.value === updatedCompanyProfile.description) return;
                const updatedProfile = new CompanyProfile({
                  ...updatedCompanyProfile,
                  description: event.target.value,
                });
                setUpdatedCompanyProfile(updatedProfile);
                validateProfile(updatedProfile);
              }}
              multiline={true}
              minRows={4}
              placeholder="Additional info displayed on your profile"
              variant="outlined"
            />
          </AboutYou>
        )}
        {!isLoading && companyId && company && (
          <CompanyDetails>
            <VisibilityToggleSwitch
              label="Show Company On Marketplace"
              checked={isVisible}
              disabled={saving}
              onChange={(nextIsVisible) => {
                setIsVisible(nextIsVisible);
              }}
            />
          </CompanyDetails>
        )}
      </FormScrollArea>
      <ButtonContainer>
        <Tooltip title={saveValidationMessage} placement="top">
          <SaveButtonWrapper>
            <LoadingButton
              id="saveButton"
              variant="contained"
              startIcon={<SaveIcon />}
              loading={saving}
              color="primary"
              disabled={!canSave()}
              onClick={handleSaveButtonClick}
            >
              {companyId ? "Save Changes" : "Create Profile"}
            </LoadingButton>
          </SaveButtonWrapper>
        </Tooltip>
        {saveValidationMessage && (
          <SaveValidationMessage variant="caption">
            {saveValidationMessage}
          </SaveValidationMessage>
        )}
      </ButtonContainer>
    </MainContainer>
  );
}
// #endregion
